<template>
  <div class="tip-stats-container">
    <div class="text-center mb-5">
      <h2 class="section-title">
        STATISTIKY PRO <span class="text-red">{{ roundNumber }}. KOLO</span>
      </h2>
      <p class="text-muted small">Podívejte se, jak tipují ostatní hráči</p>
    </div>

    <div v-if="matchStatistics.length" class="stats-grid">
      <div v-for="match in matchStatistics" :key="match.id" class="stats-card">
        <div class="match-header">
          <div class="team-col">
            <img :src="match.homeLogo" alt="Domácí" class="team-logo" />
            <span class="team-name">{{ match.homeTeam }}</span>
          </div>

          <div class="vs-badge">VS</div>

          <div class="team-col">
            <img :src="match.awayLogo" alt="Hosté" class="team-logo" />
            <span class="team-name">{{ match.awayTeam }}</span>
          </div>
        </div>

        <div class="separator-thin"></div>

        <div class="stats-body">
          <div class="d-flex justify-content-between mb-3 text-small">
            <span class="text-muted"
              >Celkem tipů:
              <strong class="text-white">{{ match.total }}</strong></span
            >
            <span class="text-muted"
              >Top výsledek:
              <strong class="text-red">{{
                match.mostFrequentScore || "-"
              }}</strong></span
            >
          </div>

          <div class="progress-section">
            <div class="progress-item">
              <div class="progress-label">
                <span>Výhra domácích</span>
                <span>{{ match.homeWinPercent }}%</span>
              </div>
              <div class="progress-track">
                <div
                  class="progress-fill home"
                  :style="{ width: match.homeWinPercent + '%' }"
                ></div>
              </div>
            </div>

            <div class="progress-item">
              <div class="progress-label">
                <span>Remíza</span>
                <span>{{ match.drawPercent }}%</span>
              </div>
              <div class="progress-track">
                <div
                  class="progress-fill draw"
                  :style="{ width: match.drawPercent + '%' }"
                ></div>
              </div>
            </div>

            <div class="progress-item">
              <div class="progress-label">
                <span>Výhra hostů</span>
                <span>{{ match.awayWinPercent }}%</span>
              </div>
              <div class="progress-track">
                <div
                  class="progress-fill away"
                  :style="{ width: match.awayWinPercent + '%' }"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="questionStatistics.total > 0"
      class="stats-card bonus-stats mt-5 mx-auto"
    >
      <div class="text-center mb-4">
        <h3 class="section-title" style="font-size: 1.2rem">
          <svg
            class="icon-sm me-2"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line x1="18" y1="20" x2="18" y2="10"></line>
            <line x1="12" y1="20" x2="12" y2="4"></line>
            <line x1="6" y1="20" x2="6" y2="14"></line>
          </svg>
          ODPOVĚDI NA OTÁZKU
        </h3>
      </div>

      <div class="bonus-bars">
        <div class="progress-item mb-3">
          <div class="progress-label">
            <span class="fw-bold text-white">ANO</span>
            <span
              >{{
                (
                  (questionStatistics.yes / questionStatistics.total) *
                  100
                ).toFixed(1)
              }}%</span
            >
          </div>
          <div class="progress-track">
            <div
              class="progress-fill yes"
              :style="{
                width:
                  (questionStatistics.yes / questionStatistics.total) * 100 +
                  '%',
              }"
            ></div>
          </div>
        </div>

        <div class="progress-item">
          <div class="progress-label">
            <span class="fw-bold text-white">NE</span>
            <span
              >{{
                (
                  (questionStatistics.no / questionStatistics.total) *
                  100
                ).toFixed(1)
              }}%</span
            >
          </div>
          <div class="progress-track">
            <div
              class="progress-fill no"
              :style="{
                width:
                  (questionStatistics.no / questionStatistics.total) * 100 +
                  '%',
              }"
            ></div>
          </div>
        </div>

        <div class="text-center mt-3 text-muted" style="font-size: 0.8rem">
          Celkem odpovědí: {{ questionStatistics.total }}
        </div>
      </div>
    </div>

    <div
      v-else-if="!loading && !matchStatistics.length"
      class="text-center mt-5 text-muted"
    >
      <p>Pro toto kolo zatím nejsou k dispozici žádné tipy.</p>
    </div>
  </div>
</template>

<script>
import { db } from "../firebase";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  orderBy,
  getDoc,
} from "firebase/firestore";

export default {
  name: "TipStatistics",
  props: {
    // Pokud předáváte roundNumber z rodiče, nechte to jako prop.
    // Pokud si to komponenta stahuje sama (jak to bylo v původním kódu),
    // nechte to v data() a fetchVisibleRound().
    // Zde nechávám původní logiku "stáhni si sám", ale přidávám watch pro reaktivitu.
    username: String,
  },
  data() {
    return {
      roundNumber: 1,
      matchStatistics: [],
      questionStatistics: {
        yes: 0,
        no: 0,
        total: 0,
      },
      loading: true,
    };
  },
  async mounted() {
    await this.fetchVisibleRound();
  },
  methods: {
    async fetchVisibleRound() {
      try {
        const roundRef = doc(db, "settings", "visibleRound");
        const roundSnap = await getDoc(roundRef);
        if (roundSnap.exists()) {
          this.roundNumber = roundSnap.data().roundNumber;
        }
        await this.fetchTipStatistics();
      } catch (error) {
        console.error("Chyba při načítání kola:", error);
      }
    },

    async fetchTipStatistics() {
      this.loading = true;
      try {
        // 1. Načíst zápasy
        const matchesQuery = query(
          collection(db, "matches"),
          where("roundNumber", "==", this.roundNumber),
          orderBy("matchDate", "asc")
        );
        const matchesSnapshot = await getDocs(matchesQuery);

        if (!matchesSnapshot.empty) {
          // 2. Načíst loga týmů
          const teamsSnapshot = await getDocs(collection(db, "teams"));
          const teamsMap = {};
          teamsSnapshot.docs.forEach((doc) => {
            const teamData = doc.data();
            teamsMap[teamData.name] = teamData.logo;
          });

          // 3. Inicializace objektů zápasů
          const matches = matchesSnapshot.docs.map((doc) => {
            const matchData = doc.data();
            return {
              id: doc.id,
              homeTeam: matchData.homeTeam,
              awayTeam: matchData.awayTeam,
              homeLogo: teamsMap[matchData.homeTeam] || "",
              awayLogo: teamsMap[matchData.awayTeam] || "",
              homeWins: 0,
              draws: 0,
              awayWins: 0,
              total: 0,
              exactScores: {}, // Pro sledování nejčastějšího skóre
            };
          });

          // 4. Načíst všechny tipy pro toto kolo
          const tipsQuery = query(
            collection(db, "tips"),
            where("roundNumber", "==", this.roundNumber)
          );
          const tipsSnapshot = await getDocs(tipsQuery);

          let yesCount = 0;
          let noCount = 0;

          // 5. Zpracování tipů
          tipsSnapshot.forEach((doc) => {
            const data = doc.data();

            // Statistika otázky
            if (data.questionAnswer === "Ano") yesCount++;
            if (data.questionAnswer === "Ne") noCount++;

            // Statistika zápasů
            if (data.tips) {
              Object.keys(data.tips).forEach((matchId) => {
                const match = matches.find((m) => m.id === matchId);
                if (match) {
                  const tip = data.tips[matchId];
                  if (
                    tip &&
                    tip.homeScore !== undefined &&
                    tip.awayScore !== undefined
                  ) {
                    match.total++;

                    const h = Number(tip.homeScore);
                    const a = Number(tip.awayScore);

                    // 1, 0, 2
                    if (h > a) match.homeWins++;
                    else if (h === a) match.draws++;
                    else match.awayWins++;

                    // Přesný výsledek
                    const scoreKey = `${h}:${a}`;
                    if (match.exactScores[scoreKey]) {
                      match.exactScores[scoreKey]++;
                    } else {
                      match.exactScores[scoreKey] = 1;
                    }
                  }
                }
              });
            }
          });

          this.questionStatistics = {
            yes: yesCount,
            no: noCount,
            total: yesCount + noCount,
          };

          // 6. Výpočet procent a finalizace
          this.matchStatistics = matches.map((match) => {
            // Najít nejčastější skóre
            let mostFrequentScore = "";
            let maxCount = 0;
            for (const [score, count] of Object.entries(match.exactScores)) {
              if (count > maxCount) {
                maxCount = count;
                mostFrequentScore = score;
              }
            }

            return {
              ...match,
              homeWinPercent: match.total
                ? ((match.homeWins / match.total) * 100).toFixed(1)
                : 0,
              drawPercent: match.total
                ? ((match.draws / match.total) * 100).toFixed(1)
                : 0,
              awayWinPercent: match.total
                ? ((match.awayWins / match.total) * 100).toFixed(1)
                : 0,
              mostFrequentScore: mostFrequentScore,
            };
          });
        }
      } catch (error) {
        console.error("Chyba statistik:", error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Grid pro karty */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 20px;
}

/* Hlavička zápasu */
.match-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.team-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 35%;
  text-align: center;
}

.team-logo {
  width: 45px;
  height: 45px;
  object-fit: contain;
  margin-bottom: 5px;
}

.team-name {
  font-size: 0.85rem;
  font-weight: bold;
  line-height: 1.1;
  color: #fff;
}

.vs-badge {
  font-size: 1rem;
  font-weight: 900;
  color: #555;
  font-style: italic;
}

.separator-thin {
  border-top: 1px solid #333;
  margin: 10px 0 15px 0;
}

/* Textové statistiky */
.text-small {
  font-size: 0.85rem;
}

/* Progress Bars */
.progress-item {
  margin-bottom: 12px;
}

.progress-label {
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem;
  color: #bbb;
  margin-bottom: 4px;
}

.progress-track {
  background: #222;
  height: 8px;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.6s cubic-bezier(0.25, 1, 0.5, 1);
}

/* Barvy grafů */
.progress-fill.home {
  background-color: #28a745;
}
.progress-fill.draw {
  background-color: #ffc107;
}
.progress-fill.away {
  background-color: #dc3545;
}

.progress-fill.yes {
  background-color: #28a745;
}
.progress-fill.no {
  background-color: #dc3545;
}

/* Bonusová karta (má max šířku, aby nebyla roztažená) */
.bonus-stats {
  max-width: 500px;
  border: 1px solid #333;
}

.icon-sm {
  width: 20px;
  height: 20px;
  vertical-align: text-bottom;
}
</style>
